import { Member, MemberFormData, MemberDataRequest } from '../types/member.types';
import { StaffApiResponse } from '../services/members.service';

import { getDepartmentCodeByName } from '@/config/search-configs';
import { OTHER_DEPARTMENT_CODE } from '@/lib/constants';

/**
 * Transform Staff API response to Member interface
 * @param staff - Staff data from API
 * @returns Member object for frontend use
 */
export const mapStaffToMember = (staff: StaffApiResponse): Member => {
  // Get main department name
  const department = staff.department?.name || '';

  const isSelectable = staff.isSelectable || false;

  // Get role names and combine them
  const roleNames = staff.roles?.map((r) => r.role.name) || [];
  const role = roleNames.length > 0 ? roleNames.join(' / ') : '';

  // Get related department names
  const relatedDepartments = staff.relatedStaffDepartments?.map((rd) => rd.department.name) || [];

  // Determine status based on isDeleted flag
  const status: '登録済' | '削除済' = staff.isDeleted ? '削除済' : '登録済';

  return {
    id: staff.id,
    lastName: staff.lastName,
    firstName: staff.firstName,
    email: staff.email,
    isSelectable: isSelectable,
    role,
    department,
    relatedDepartments,
    status,
    updatedAt: staff.updatedAt || '',
    createdAt: staff.createdAt || '',
  };
};

/**
 * Transform array of Staff API responses to Member array
 * @param staffs - Array of staff data from API
 * @returns Array of Member objects
 */
export const mapStaffsToMembers = (staffs: StaffApiResponse[]): Member[] => {
  return staffs.map(mapStaffToMember);
};

/**
 * Convert frontend search params to API params
 * @param searchParams - Frontend search parameters
 * @returns API parameters object
 */
export const mapSearchParamsToApiParams = (
  searchParams: Record<string, unknown>
): Record<string, unknown> => {
  const apiParams: Record<string, unknown> = {};

  // Map status to isDeleted
  if (searchParams.status) {
    apiParams.isDeleted = searchParams.status === '削除済';
  }

  // Map name field to lastName (search config uses 'name' but API expects 'lastName')
  if (searchParams.name) {
    apiParams.lastName = searchParams.name;
  }

  // Also handle direct lastName if passed
  if (searchParams.lastName) {
    apiParams.lastName = searchParams.lastName;
  }

  // Map department name to bucode (department code)
  if (searchParams.department) {
    const departmentText = String(searchParams.department).trim();
    const departmentCode = getDepartmentCodeByName(departmentText);
    if (departmentCode !== undefined) {
      apiParams.bucode = departmentCode;
    } else if (departmentText) {
      apiParams.departmentName = departmentText;
    }
  }

  if (searchParams.bucode) {
    apiParams.bucode = searchParams.bucode;
  }

  if (searchParams.roleKeys) {
    apiParams.roleKeys = searchParams.roleKeys;
  }

  return apiParams;
};

export const mapMemberFormDataToApi = (formData: MemberFormData): MemberDataRequest => {
  const relatedDepartments = formData.relatedStaffDepartmentCodes || [];

  if (formData.isOtherRelatedDepartment) {
    if (!relatedDepartments.includes(OTHER_DEPARTMENT_CODE)) {
      relatedDepartments.push(OTHER_DEPARTMENT_CODE);
    }
  }
  return {
    ...formData,
    email: formData.email?.trim() || '',
    code: Number(formData.code),
    bucode: Number(formData.bucode),
    relatedStaffDepartmentCodes: relatedDepartments,
  } as MemberDataRequest;
};
